import { useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  Globe,
  Linkedin,
  Mail,
  Phone,
  MapPin,
  Users,
  Banknote,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  Loader2,
  ExternalLink,
  Calculator,
  Plus,
  Search,
  X,
  UserPlus,
  Link2,
  Target,
  StickyNote,
  Sparkles,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn, daysSince } from "@/lib/utils";

type IndustryType = "accounting" | "business";

const INDUSTRY_TYPE_OPTIONS: { value: IndustryType; label: string; tone: string }[] = [
  { value: "accounting", label: "Accounting", tone: "bg-blue-100 text-blue-700 border-blue-300" },
  { value: "business", label: "Business", tone: "bg-amber-100 text-amber-800 border-amber-300" },
];
const INDUSTRY_TYPE_BY_VALUE = Object.fromEntries(
  INDUSTRY_TYPE_OPTIONS.map((o) => [o.value, o] as const)
) as Record<IndustryType, (typeof INDUSTRY_TYPE_OPTIONS)[number]>;

export function ClientDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const client = trpc.clients.get.useQuery(
    { id: id! },
    {
      enabled: !!id,
      refetchInterval: (q) =>
        q.state.data?.enrichmentStatus === "running" ||
        q.state.data?.enrichmentStatus === "pending"
          ? 3000
          : false,
    }
  );

  const requestEnrichment = trpc.clients.requestEnrichment.useMutation({
    onSuccess: () => {
      toast.success("Profile preparation started");
      utils.clients.get.invalidate({ id: id! });
      utils.clients.list.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const updateClient = trpc.clients.update.useMutation({
    onSuccess: () => {
      utils.clients.get.invalidate({ id: id! });
      utils.clients.list.invalidate();
      utils.opportunities.list.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const deleteClient = trpc.clients.delete.useMutation({
    onSuccess: () => {
      toast.success("Client deleted");
      utils.clients.list.invalidate();
      utils.opportunities.list.invalidate();
      navigate("/clients");
    },
    onError: (err) => toast.error(err.message ?? "Failed to delete client"),
  });

  if (client.isLoading) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-10 w-1/3" />
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (client.isError || !client.data) {
    return (
      <div className="p-6">
        <p className="text-sm text-destructive">Failed to load client.</p>
      </div>
    );
  }

  const c = client.data;
  const profile = c.profile;

  const statusBadge = {
    pending: { label: "Queued", icon: Loader2, tone: "bg-zinc-100 text-zinc-700" },
    running: { label: "Preparing...", icon: Loader2, tone: "bg-blue-100 text-blue-700" },
    succeeded: { label: "Ready", icon: CheckCircle2, tone: "bg-emerald-100 text-emerald-700" },
    failed: { label: "Error", icon: AlertCircle, tone: "bg-rose-100 text-rose-700" },
  }[c.enrichmentStatus];
  const StatusIcon = statusBadge.icon;

  return (
    <div className="mx-auto max-w-screen-xl space-y-4 px-4 py-6 md:px-6">
      <div className="flex items-center justify-between gap-3">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/clients">
            <ArrowLeft className="h-4 w-4" />
            Clients
          </Link>
        </Button>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className={cn(statusBadge.tone)}>
            <StatusIcon
              className={cn(
                "mr-1 h-3 w-3",
                (c.enrichmentStatus === "running" || c.enrichmentStatus === "pending") &&
                  "animate-spin"
              )}
            />
            {statusBadge.label}
          </Badge>
          <Button
            size="sm"
            variant="outline"
            disabled={requestEnrichment.isPending || c.enrichmentStatus === "running"}
            onClick={() => requestEnrichment.mutate({ id: c.id })}
          >
            <RefreshCw className={cn("h-3 w-3", requestEnrichment.isPending && "animate-spin")} />
            Refresh profile
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                className="h-8 w-8 text-muted-foreground hover:text-destructive"
                disabled={deleteClient.isPending}
                aria-label="Delete client"
                title="Delete client"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete client?</AlertDialogTitle>
                <AlertDialogDescription>
                  This permanently deletes <strong>{c.companyName}</strong> and its accounting system
                  assignments. Related contacts, opportunities, and documents will be kept but
                  unlinked from this client. This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  onClick={() => deleteClient.mutate({ id: c.id })}
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <div className="flex items-start gap-4">
        <div className="grid h-12 w-12 place-items-center rounded-lg bg-gradient-to-br from-blue-500 to-cyan-400 text-white">
          <Building2 className="h-6 w-6" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-semibold tracking-tight">{c.companyName}</h1>
            {c.industryType && (
              <span
                className={cn(
                  "rounded-full border px-2 py-0.5 text-xs font-semibold",
                  INDUSTRY_TYPE_BY_VALUE[c.industryType as IndustryType].tone
                )}
              >
                {INDUSTRY_TYPE_BY_VALUE[c.industryType as IndustryType].label}
              </span>
            )}
            {c.fiberyExportedAt && (
              <Badge variant="secondary" className="bg-violet-100 text-violet-700">
                Exported to Fibery
              </Badge>
            )}
          </div>
          {c.legalForm && (
            <p className="text-sm text-muted-foreground">
              {c.legalForm}
              {c.regCode && ` · ${c.regCode}`}
              {c.foundedYear && ` · founded ${c.foundedYear}`}
            </p>
          )}
          <div className="mt-2 flex items-center gap-2">
            <Label className="text-xs text-muted-foreground">Type</Label>
            <Select
              value={c.industryType ?? "none"}
              onValueChange={(value) =>
                updateClient.mutate({
                  id: c.id,
                  patch: { industryType: value === "none" ? null : (value as IndustryType) },
                })
              }
            >
              <SelectTrigger className="h-7 w-[160px] text-xs">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Not set —</SelectItem>
                {INDUSTRY_TYPE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {c.enrichmentError && (
        <Card className="border-rose-200 bg-rose-50/50">
          <CardContent className="py-3 text-sm text-rose-700">
            <strong>Preparation error:</strong> {c.enrichmentError}
          </CardContent>
        </Card>
      )}

      {profile?.summary && (
        <Card>
          <CardContent className="py-4 text-sm leading-relaxed">{profile.summary}</CardContent>
        </Card>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        {/* Left column */}
        <div className="space-y-4 lg:col-span-2">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
                Key information
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
              <Field label="Industry" value={c.industry} />
              <Field label="Accounting system" value={c.accountingSystem} />
              <Field
                label="Headcount"
                value={c.headcount ? `${c.headcount}${c.headcountSource ? ` (${c.headcountSource})` : ""}` : null}
              />
              <Field
                label="Revenue"
                value={
                  c.revenueEur
                    ? `€${c.revenueEur.toLocaleString("en-US")}${c.revenueYear ? ` (${c.revenueYear})` : ""}`
                    : null
                }
              />
              <Field label="Geography" value={profile?.geographicReach} />
              <Field label="Pricing" value={profile?.pricingModel} />
            </CardContent>
          </Card>

          {profile?.services && profile.services.length > 0 && (
            <ListCard title="Services" items={profile.services} />
          )}
          {profile?.clientSegments && profile.clientSegments.length > 0 && (
            <ListCard title="Client segments" items={profile.clientSegments} />
          )}

          <ClientOpportunities clientId={c.id} />

          <ClientAccountingSystems clientId={c.id} />

          <ClientContacts clientId={c.id} />

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
                Technology
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <ChipRow label="Tax software" items={profile?.taxSoftware} />
              <ChipRow label="Practice management" items={profile?.practiceManagement} />
              <ChipRow label="Document management" items={profile?.documentManagement} />
              <ChipRow label="Other tech" items={profile?.technologies} />
            </CardContent>
          </Card>

          {profile?.decisionMakers && profile.decisionMakers.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
                  Decision makers
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {profile.decisionMakers.map((dm, i) => (
                  <div key={i} className="rounded-md border p-3">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <div>
                        <div className="font-medium">{dm.name}</div>
                        {dm.title && (
                          <div className="text-xs text-muted-foreground">{dm.title}</div>
                        )}
                      </div>
                      {dm.linkedinUrl && (
                        <a
                          href={dm.linkedinUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
                        >
                          <Linkedin className="h-3 w-3" />
                          LinkedIn
                        </a>
                      )}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      {dm.email && (
                        <a href={`mailto:${dm.email}`} className="inline-flex items-center gap-1 hover:text-foreground">
                          <Mail className="h-3 w-3" />
                          {dm.email}
                        </a>
                      )}
                      {dm.phone && (
                        <a href={`tel:${dm.phone}`} className="inline-flex items-center gap-1 hover:text-foreground">
                          <Phone className="h-3 w-3" />
                          {dm.phone}
                        </a>
                      )}
                      {dm.source && <span className="opacity-70">source: {dm.source}</span>}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {profile?.recentNews && profile.recentNews.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
                  Recent news
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {profile.recentNews.map((n, i) => (
                  <div key={i} className="border-l-2 pl-3">
                    <div className="flex items-baseline justify-between gap-2">
                      <div className="font-medium text-sm">{n.title}</div>
                      {n.date && (
                        <div className="shrink-0 text-xs text-muted-foreground">{n.date}</div>
                      )}
                    </div>
                    {n.summary && (
                      <p className="mt-1 text-xs text-muted-foreground">{n.summary}</p>
                    )}
                    {n.url && (
                      <a
                        href={n.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-1 inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
                      >
                        <ExternalLink className="h-3 w-3" />
                        read
                      </a>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {profile?.buyingSignals && profile.buyingSignals.length > 0 && (
            <ListCard title="Buying signals" items={profile.buyingSignals} highlight />
          )}
          {profile?.openQuestions && profile.openQuestions.length > 0 && (
            <ListCard title="Open questions" items={profile.openQuestions} muted />
          )}
        </div>

        {/* Right column */}
        <aside className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
                Contacts
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2.5 text-sm">
              {c.website && (
                <a
                  href={c.website.startsWith("http") ? c.website : `https://${c.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 hover:text-blue-600 hover:underline"
                >
                  <Globe className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="truncate">{c.website}</span>
                </a>
              )}
              {c.linkedinUrl && (
                <a
                  href={c.linkedinUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 hover:text-blue-600 hover:underline"
                >
                  <Linkedin className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="truncate">LinkedIn</span>
                </a>
              )}
              {c.email && (
                <a
                  href={`mailto:${c.email}`}
                  className="flex items-center gap-2 hover:text-blue-600 hover:underline"
                >
                  <Mail className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="truncate">{c.email}</span>
                </a>
              )}
              {c.phone && (
                <a
                  href={`tel:${c.phone}`}
                  className="flex items-center gap-2 hover:text-blue-600 hover:underline"
                >
                  <Phone className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span>{c.phone}</span>
                </a>
              )}
              {(c.addressStreet || c.city) && (
                <div className="flex items-start gap-2">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <div>
                    {c.addressStreet && <div>{c.addressStreet}</div>}
                    {(c.postalCode || c.city) && (
                      <div className="text-muted-foreground">
                        {[c.postalCode, c.city].filter(Boolean).join(" ")}
                      </div>
                    )}
                  </div>
                </div>
              )}
              {c.regCode && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Users className="h-4 w-4" />
                  <span>Reg. #: {c.regCode}</span>
                </div>
              )}
              {c.vatCode && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Banknote className="h-4 w-4" />
                  <span>VAT: {c.vatCode}</span>
                </div>
              )}
            </CardContent>
          </Card>

          {profile?.socialLinks && Object.values(profile.socialLinks).some(Boolean) && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
                  Social links
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1.5 text-sm">
                {Object.entries(profile.socialLinks).map(([key, url]) =>
                  url ? (
                    <a
                      key={key}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block truncate text-blue-600 hover:underline"
                    >
                      {key}: {url}
                    </a>
                  ) : null
                )}
              </CardContent>
            </Card>
          )}

          {profile?.certifications && profile.certifications.length > 0 && (
            <ListCard title="Certifications" items={profile.certifications} />
          )}

          {profile?.sources && profile.sources.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
                  Sources
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1.5 text-xs">
                {profile.sources.map((s, i) => (
                  <a
                    key={i}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block truncate text-blue-600 hover:underline"
                  >
                    {s.title || s.url}
                  </a>
                ))}
              </CardContent>
            </Card>
          )}
        </aside>
      </div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={cn("text-sm", value ? "" : "text-muted-foreground/50")}>{value || "—"}</div>
    </div>
  );
}

function ListCard({
  title,
  items,
  highlight,
  muted,
}: {
  title: string;
  items: string[];
  highlight?: boolean;
  muted?: boolean;
}) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="space-y-1.5">
          {items.map((it, i) => (
            <li
              key={i}
              className={cn(
                "text-sm",
                highlight && "font-medium text-emerald-700",
                muted && "text-muted-foreground"
              )}
            >
              · {it}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

function ClientAccountingSystems({ clientId }: { clientId: string }) {
  const utils = trpc.useUtils();
  const allQuery = trpc.accountingSystems.list.useQuery();
  const assignedQuery = trpc.accountingSystems.listForClient.useQuery({ clientId });
  const setMutation = trpc.accountingSystems.setForClient.useMutation({
    onSuccess: () => {
      utils.accountingSystems.listForClient.invalidate({ clientId });
    },
    onError: () => toast.error("Failed to save"),
  });

  const [search, setSearch] = useState("");

  const all = allQuery.data ?? [];
  const assigned = assignedQuery.data ?? [];
  const assignedIds = new Set(assigned.map((s) => s.id));

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return all;
    return all.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        (s.country ?? "").toLowerCase().includes(q)
    );
  }, [all, search]);

  const toggle = (id: string, checked: boolean) => {
    const next = checked
      ? [...assignedIds, id]
      : [...assignedIds].filter((x) => x !== id);
    setMutation.mutate({ clientId, accountingSystemIds: next });
  };

  const remove = (id: string) => {
    const next = [...assignedIds].filter((x) => x !== id);
    setMutation.mutate({ clientId, accountingSystemIds: next });
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="flex items-center gap-2 text-sm font-medium uppercase tracking-wide text-muted-foreground">
          <Calculator className="h-3.5 w-3.5" />
          Accounting systems
        </CardTitle>
        <DropdownMenu onOpenChange={(open) => { if (!open) setSearch(""); }}>
          <DropdownMenuTrigger asChild>
            <Button size="sm" variant="outline" className="h-7 gap-1 text-xs">
              <Plus className="h-3 w-3" />
              Assign
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64 p-0">
            <DropdownMenuLabel>Accounting systems</DropdownMenuLabel>
            <div className="px-2 pb-2">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  autoFocus
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()}
                  placeholder="Search…"
                  className="h-7 pl-7 text-xs"
                />
              </div>
            </div>
            <DropdownMenuSeparator />
            <div className="max-h-64 overflow-y-auto">
              {all.length === 0 ? (
                <div className="px-2 py-3 text-xs text-muted-foreground">
                  No systems. Create them on the "Accounting systems" page.
                </div>
              ) : filtered.length === 0 ? (
                <div className="px-2 py-3 text-xs text-muted-foreground">
                  Nothing found.
                </div>
              ) : (
                filtered.map((s) => (
                  <DropdownMenuCheckboxItem
                    key={s.id}
                    checked={assignedIds.has(s.id)}
                    onSelect={(e) => e.preventDefault()}
                    onCheckedChange={(checked) => toggle(s.id, !!checked)}
                  >
                    <span className="flex-1">{s.name}</span>
                    <span className="ml-2 text-xs text-muted-foreground">{s.country}</span>
                  </DropdownMenuCheckboxItem>
                ))
              )}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      </CardHeader>
      <CardContent>
        {assignedQuery.isLoading ? (
          <Skeleton className="h-6 w-48" />
        ) : assigned.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No accounting systems assigned.
          </p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {assigned.map((s) => (
              <Badge
                key={s.id}
                variant="secondary"
                className="gap-1 pl-2 pr-1 text-xs font-normal"
              >
                {s.name}
                <button
                  type="button"
                  onClick={() => remove(s.id)}
                  className="rounded-full p-0.5 text-secondary-foreground/80 hover:bg-white/20 hover:text-secondary-foreground"
                  aria-label={`Remove ${s.name}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

const OPPORTUNITY_STATUS_META: Record<
  string,
  { label: string; tone: string }
> = {
  opportunity: { label: "Opportunity", tone: "bg-slate-100 text-slate-700" },
  ready: { label: "Ready", tone: "bg-zinc-100 text-zinc-700" },
  approaching: { label: "Approaching", tone: "bg-sky-100 text-sky-700" },
  quote_sent: { label: "Quote sent", tone: "bg-blue-100 text-blue-700" },
  negotiation: { label: "Negotiation", tone: "bg-amber-100 text-amber-800" },
  signing: { label: "Signing", tone: "bg-violet-100 text-violet-800" },
  won: { label: "Won", tone: "bg-emerald-100 text-emerald-800" },
  lost: { label: "Lost", tone: "bg-rose-100 text-rose-700" },
};

function ClientOpportunities({ clientId }: { clientId: string }) {
  const list = trpc.opportunities.list.useQuery({ clientId });
  const rows = list.data ?? [];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="flex items-center gap-2 text-sm font-medium uppercase tracking-wide text-muted-foreground">
          <Target className="h-3.5 w-3.5" />
          Opportunities ({rows.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {list.isLoading ? (
          <Skeleton className="h-16 w-full" />
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No opportunities yet.</p>
        ) : (
          rows.map((o) => {
            const meta = OPPORTUNITY_STATUS_META[o.status] ?? {
              label: o.status,
              tone: "bg-zinc-100 text-zinc-700",
            };
            const days = daysSince(o.updatedAt);
            return (
              <Link
                key={o.id}
                to={`/o/${o.id}`}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border bg-background/60 px-3 py-2 text-sm hover:bg-muted/40"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate font-medium">{o.name}</span>
                    <Badge
                      variant="secondary"
                      className={cn("shrink-0 text-[10px] font-normal", meta.tone)}
                    >
                      {meta.label}
                    </Badge>
                  </div>
                  <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-muted-foreground">
                    {o.estimatedPlan && <span>{o.estimatedPlan}</span>}
                    {o.estimatedSeats != null && <span>{o.estimatedSeats} seats</span>}
                    {o.estimatedMrrEur != null && (
                      <span className="font-medium text-foreground">
                        €{o.estimatedMrrEur}/mo
                      </span>
                    )}
                    {days != null && (
                      <span>{days === 0 ? "today" : `${days}d ago`}</span>
                    )}
                    {o.noteCount != null && o.noteCount > 0 && (
                      <span className="inline-flex items-center gap-1">
                        <StickyNote className="h-3 w-3" />
                        {o.noteCount}
                      </span>
                    )}
                  </div>
                </div>
              </Link>
            );
          })
        )}
      </CardContent>
    </Card>
  );
}

function ChipRow({ label, items }: { label: string; items: string[] | undefined }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <div className="mb-1 text-xs text-muted-foreground">{label}</div>
      <div className="flex flex-wrap gap-1.5">
        {items.map((it, i) => (
          <Badge key={i} variant="secondary" className="font-normal">
            {it}
          </Badge>
        ))}
      </div>
    </div>
  );
}

function ClientContacts({ clientId }: { clientId: string }) {
  const utils = trpc.useUtils();
  const list = trpc.contacts.listByClient.useQuery({ clientId });
  const rows = list.data ?? [];

  const [createOpen, setCreateOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [findMoreOpen, setFindMoreOpen] = useState(false);

  const unassign = trpc.contacts.assignToClient.useMutation({
    onSuccess: () => {
      toast.success("Contact unlinked");
      utils.contacts.listByClient.invalidate({ clientId });
      utils.contacts.list.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
          Contacts ({rows.length})
        </CardTitle>
        <div className="flex items-center gap-1.5">
          <Button
            size="sm"
            variant="outline"
            className="h-7 gap-1 text-xs"
            onClick={() => setFindMoreOpen(true)}
          >
            <Sparkles className="h-3 w-3" />
            Find more
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline" className="h-7 gap-1 text-xs">
                <Plus className="h-3 w-3" />
                Add
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onSelect={() => setCreateOpen(true)}>
                <UserPlus className="mr-2 h-3.5 w-3.5" />
                Create new
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setAssignOpen(true)}>
                <Link2 className="mr-2 h-3.5 w-3.5" />
                Assign existing
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {list.isLoading ? (
          <Skeleton className="h-16 w-full" />
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No contacts.</p>
        ) : (
          rows.map((c) => {
            const name = [c.firstName, c.lastName].filter(Boolean).join(" ") || c.email || "—";
            return (
              <div
                key={c.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border bg-background/60 px-3 py-2 text-sm"
              >
                <div className="min-w-0">
                  <div className="font-medium">{name}</div>
                  {c.jobTitle && (
                    <div className="text-xs text-muted-foreground">{c.jobTitle}</div>
                  )}
                </div>
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  {c.email && (
                    <a className="hover:underline" href={`mailto:${c.email}`}>
                      {c.email}
                    </a>
                  )}
                  {(c.phone || c.mobilePhone) && (
                    <a className="hover:underline" href={`tel:${c.phone ?? c.mobilePhone ?? ""}`}>
                      {c.phone ?? c.mobilePhone}
                    </a>
                  )}
                  <button
                    type="button"
                    onClick={() => unassign.mutate({ id: c.id, clientId: null })}
                    disabled={unassign.isPending}
                    className="rounded-full p-0.5 text-muted-foreground hover:bg-muted-foreground/10 hover:text-foreground disabled:opacity-50"
                    aria-label={`Unlink ${name}`}
                    title="Unlink from client"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </CardContent>

      <CreateContactDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        clientId={clientId}
      />
      <AssignContactDialog
        open={assignOpen}
        onOpenChange={setAssignOpen}
        clientId={clientId}
      />
      <FindMoreContactsDialog
        open={findMoreOpen}
        onOpenChange={setFindMoreOpen}
        clientId={clientId}
      />
    </Card>
  );
}

type ContactFormState = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  mobilePhone: string;
  jobTitle: string;
  linkedinUrl: string;
};

const emptyContactForm: ContactFormState = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  mobilePhone: "",
  jobTitle: "",
  linkedinUrl: "",
};

function CreateContactDialog({
  open,
  onOpenChange,
  clientId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string;
}) {
  const utils = trpc.useUtils();
  const [form, setForm] = useState<ContactFormState>(emptyContactForm);

  const create = trpc.contacts.create.useMutation({
    onSuccess: () => {
      toast.success("Contact created");
      utils.contacts.listByClient.invalidate({ clientId });
      utils.contacts.list.invalidate();
      setForm(emptyContactForm);
      onOpenChange(false);
    },
    onError: (err) => toast.error(err.message),
  });

  const set = (key: keyof ContactFormState) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleOpenChange = (next: boolean) => {
    if (!next) setForm(emptyContactForm);
    onOpenChange(next);
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const firstName = form.firstName.trim();
    const lastName = form.lastName.trim();
    const email = form.email.trim();
    if (!firstName && !lastName && !email) {
      toast.error("Need at least first name, last name, or email.");
      return;
    }
    create.mutate({
      clientId,
      firstName: firstName || null,
      lastName: lastName || null,
      email: email || null,
      phone: form.phone.trim() || null,
      mobilePhone: form.mobilePhone.trim() || null,
      jobTitle: form.jobTitle.trim() || null,
      linkedinUrl: form.linkedinUrl.trim() || null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New contact</DialogTitle>
          <DialogDescription>
            Create a contact and assign it to this client.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="contact-firstName">First name</Label>
              <Input
                id="contact-firstName"
                value={form.firstName}
                onChange={set("firstName")}
                autoFocus
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="contact-lastName">Last name</Label>
              <Input
                id="contact-lastName"
                value={form.lastName}
                onChange={set("lastName")}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="contact-jobTitle">Title</Label>
            <Input
              id="contact-jobTitle"
              value={form.jobTitle}
              onChange={set("jobTitle")}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="contact-email">Email</Label>
            <Input
              id="contact-email"
              type="email"
              value={form.email}
              onChange={set("email")}
            />
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="contact-phone">Phone</Label>
              <Input
                id="contact-phone"
                value={form.phone}
                onChange={set("phone")}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="contact-mobilePhone">Mobile</Label>
              <Input
                id="contact-mobilePhone"
                value={form.mobilePhone}
                onChange={set("mobilePhone")}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="contact-linkedin">LinkedIn URL</Label>
            <Input
              id="contact-linkedin"
              type="url"
              placeholder="https://www.linkedin.com/in/..."
              value={form.linkedinUrl}
              onChange={set("linkedinUrl")}
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => handleOpenChange(false)}
              disabled={create.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AssignContactDialog({
  open,
  onOpenChange,
  clientId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string;
}) {
  const utils = trpc.useUtils();
  const [search, setSearch] = useState("");

  const listQuery = trpc.contacts.list.useQuery(
    { search: search.trim() || undefined },
    { enabled: open }
  );

  const assign = trpc.contacts.assignToClient.useMutation({
    onSuccess: () => {
      toast.success("Contact assigned");
      utils.contacts.listByClient.invalidate({ clientId });
      utils.contacts.list.invalidate();
      onOpenChange(false);
      setSearch("");
    },
    onError: (err) => toast.error(err.message),
  });

  const rows = useMemo(() => {
    const all = listQuery.data ?? [];
    return all.filter((r) => r.clientId !== clientId);
  }, [listQuery.data, clientId]);

  const handleOpenChange = (next: boolean) => {
    if (!next) setSearch("");
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Assign existing contact</DialogTitle>
          <DialogDescription>
            Pick a contact from the list. If they are assigned to another client,
            the assignment will be moved.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, email, phone…"
              className="pl-8"
            />
          </div>
          <div className="max-h-80 overflow-y-auto rounded-md border">
            {listQuery.isLoading ? (
              <div className="p-3">
                <Skeleton className="h-16 w-full" />
              </div>
            ) : rows.length === 0 ? (
              <div className="p-4 text-center text-sm text-muted-foreground">
                {search.trim() ? "Nothing found." : "No contacts."}
              </div>
            ) : (
              <ul className="divide-y">
                {rows.map((r) => {
                  const name =
                    [r.firstName, r.lastName].filter(Boolean).join(" ") ||
                    r.email ||
                    "—";
                  const alreadyAssignedElsewhere =
                    r.clientId && r.clientId !== clientId;
                  return (
                    <li
                      key={r.id}
                      className="flex items-center justify-between gap-2 px-3 py-2 text-sm hover:bg-muted/40"
                    >
                      <div className="min-w-0">
                        <div className="font-medium">{name}</div>
                        <div className="flex flex-wrap gap-x-3 text-xs text-muted-foreground">
                          {r.jobTitle && <span>{r.jobTitle}</span>}
                          {r.email && <span>{r.email}</span>}
                          {alreadyAssignedElsewhere && r.clientName && (
                            <span className="text-amber-600">
                              now: {r.clientName}
                            </span>
                          )}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={assign.isPending}
                        onClick={() =>
                          assign.mutate({ id: r.id, clientId })
                        }
                      >
                        Assign
                      </Button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => handleOpenChange(false)}
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FindMoreContactsDialog({
  open,
  onOpenChange,
  clientId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string;
}) {
  const utils = trpc.useUtils();
  const [hasRun, setHasRun] = useState(false);
  const [addedKeys, setAddedKeys] = useState<Set<string>>(new Set());

  const find = trpc.contacts.findForClient.useMutation({
    onError: (err) => toast.error(err.message),
  });

  const create = trpc.contacts.create.useMutation({
    onSuccess: () => {
      utils.contacts.listByClient.invalidate({ clientId });
      utils.contacts.list.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const reset = () => {
    setHasRun(false);
    setAddedKeys(new Set());
    find.reset();
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handleRun = async () => {
    setAddedKeys(new Set());
    setHasRun(true);
    await find.mutateAsync({ clientId }).catch(() => {});
  };

  const handleAdd = async (
    candidate: {
      firstName: string | null;
      lastName: string | null;
      jobTitle: string | null;
      email: string | null;
      phone: string | null;
      linkedinUrl: string | null;
    },
    key: string
  ) => {
    try {
      await create.mutateAsync({
        clientId,
        firstName: candidate.firstName,
        lastName: candidate.lastName,
        email: candidate.email,
        phone: candidate.phone,
        jobTitle: candidate.jobTitle,
        linkedinUrl: candidate.linkedinUrl,
      });
      setAddedKeys((prev) => {
        const next = new Set(prev);
        next.add(key);
        return next;
      });
      toast.success("Contact added");
    } catch {
      // toast handled in onError
    }
  };

  const result = find.data;
  const candidates = result?.candidates ?? [];

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Find more contacts</DialogTitle>
          <DialogDescription>
            Search Apollo.io and Hunter.io for decision makers at this company.
          </DialogDescription>
        </DialogHeader>

        {!hasRun ? (
          <div className="space-y-3 py-2 text-sm text-muted-foreground">
            <p>
              We'll query Apollo.io and Hunter.io (where available) using the
              company name and website domain to surface executives and other
              decision makers.
            </p>
          </div>
        ) : find.isPending ? (
          <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Searching Apollo and Hunter…
          </div>
        ) : !result ? (
          <div className="py-6 text-center text-sm text-muted-foreground">
            Search failed. Try again.
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span>
                Providers used:{" "}
                {result.providers.length > 0 ? result.providers.join(", ") : "none configured"}
              </span>
              {result.domain && (
                <span className="opacity-70">· domain: {result.domain}</span>
              )}
            </div>
            <div className="max-h-96 overflow-y-auto rounded-md border">
              {candidates.length === 0 ? (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  No new contacts found.
                </div>
              ) : (
                <ul className="divide-y">
                  {candidates.map((c, i) => {
                    const key = `${c.email ?? ""}|${c.linkedinUrl ?? ""}|${
                      c.firstName ?? ""
                    } ${c.lastName ?? ""}|${i}`;
                    const name =
                      [c.firstName, c.lastName].filter(Boolean).join(" ") ||
                      c.email ||
                      "—";
                    const added = addedKeys.has(key);
                    return (
                      <li
                        key={key}
                        className="flex items-start justify-between gap-3 px-3 py-2 text-sm"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-medium">{name}</span>
                            <span className="rounded-sm border bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                              {c.source}
                            </span>
                          </div>
                          {c.jobTitle && (
                            <div className="text-xs text-muted-foreground">
                              {c.jobTitle}
                            </div>
                          )}
                          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                            {c.email && <span>{c.email}</span>}
                            {c.phone && <span>{c.phone}</span>}
                            {c.linkedinUrl && (
                              <a
                                href={c.linkedinUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline"
                              >
                                LinkedIn
                              </a>
                            )}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant={added ? "ghost" : "outline"}
                          disabled={added || create.isPending}
                          onClick={() => handleAdd(c, key)}
                        >
                          {added ? "Added" : "Add"}
                        </Button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => handleOpenChange(false)}
          >
            Close
          </Button>
          <Button
            type="button"
            onClick={handleRun}
            disabled={find.isPending}
          >
            {find.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {hasRun ? "Search again" : "Start search"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
